import { Box, Card, Divider, Stack } from '@mui/material';
import { nanoid } from 'nanoid';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import { sendMessage } from '../../firebase/actions/firebaseCreate';
import { ChatMessageInput, ChatMessageList } from '../../fl-sections/@dashboard/chat';
import { firebaseCollection } from '../../firebase/constants';
import { db } from '../../firebase/firebase';
import { deleteMessage } from '../../firebase/actions/firebaseUpdate';

export default function NewChat({ chatMessage, classId, classDetails, isChatLoading }) {
  const [messages, setMessages] = useState(chatMessage);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const { enqueueSnackbar } = useSnackbar();

  const handleSendMessage = (message) => {
    const msgObj = {
      _id: nanoid(),
      text: message,
      isDeleted: false,
      user: {
        _id: currentUser.id,
        name: currentUser?.fullName ?? 'Guest',
        avatar: currentUser?.instituteProfileImage ?? currentUser?.profileImage
      }
    };
    sendMessage(classId, msgObj).catch(() =>
      enqueueSnackbar('Something went wrong please try again', {
        variant: 'error'
      })
    );
  };

  const sendImageMessage = (fileUrl, fileName, fileType) => {
    const msgObj = {
      _id: nanoid(),
      file: fileUrl,
      fileName,
      fileType,
      isDeleted: false,
      user: {
        _id: currentUser.id,
        name: currentUser?.fullName ?? 'Guest',
        avatar: currentUser?.instituteProfileImage ?? currentUser?.profileImage
      }
    };
    sendMessage(classId, msgObj).catch(() =>
      enqueueSnackbar('Something went wrong please try again', {
        variant: 'error'
      })
    );
  };

  useEffect(() => {
    const q = query(collection(db, `${firebaseCollection.CHAT_MESSAGE}${classId}`));
    const unsub = onSnapshot(
      q,
      (doc) => {
        doc.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const findIndex = messages.findIndex((mes) => mes._id === change.doc.data()._id);
            if (findIndex === -1) {
              setMessages((prevMess) => [
                ...prevMess,
                { ...change.doc.data(), firebaseId: change.doc.id }
              ]);
              // messageContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
          }
          if (change.type === 'modified') {
            const updatedMess = [...messages];
            const findIndex = updatedMess.findIndex((mes) => mes._id === change.doc.data()._id);
            if (findIndex > -1) {
              updatedMess[findIndex] = change.doc.data();
              setMessages([...updatedMess]);
            }
          }
        });
      },
      () => {
        enqueueSnackbar('Something went wrong, can you reopen chat', {
          variant: 'error'
        });
      }
    );

    return unsub;
  }, [messages]);

  const handleDeleteMessage = (mesId) => {
    deleteMessage(classId, mesId).catch(() =>
      enqueueSnackbar('There is some error while deleting', {
        variant: 'error'
      })
    );
  };

  return (
    <Card>
      <Stack sx={{ flexGrow: 1, minWidth: '1px' }}>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            overflow: 'hidden',
            minHeight: `650px`,
            maxHeight: `650px`
          }}
        >
          <Stack sx={{ flexGrow: 1 }}>
            <ChatMessageList
              isChatLoading={isChatLoading}
              conversation={messages}
              deleteMessage={handleDeleteMessage}
            />

            <Divider />

            <ChatMessageInput
              sendImageMessage={sendImageMessage}
              onSend={handleSendMessage}
              classDetails={classDetails}
              classId={classId}
            />
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
}

NewChat.defaultProps = {
  chatMessage: []
};

NewChat.propTypes = {
  chatMessage: PropTypes.array,
  classId: PropTypes.string,
  classDetails: PropTypes.object,
  isChatLoading: PropTypes.bool
};
